import { apiClient } from '../client';
import { API_ENDPOINTS, CACHE_CONFIG } from '../config';

interface UserProfile {
  id: string;
  name: string;
  email: string;
  memberSince: string;
  profileImage: string;
  subscription: any;
  notifications: any;
  linkedAccounts: any[];
  stats: {
    savedProviders: number;
    claimsSubmitted: number;
    savingsAchieved: string;
  };
}

interface ContactInfo {
  personalEmail: string;
  phoneNumber: string;
  workEmail?: string;
}

interface NotificationSettings {
  email: Record<string, boolean>;
  sms: Record<string, boolean>;
  push: Record<string, boolean>;
}

interface PaymentMethod {
  id: string;
  type: string;
  last4: string;
  expiryMonth: number;
  expiryYear: number;
  isDefault: boolean;
  holderName: string;
}

interface LinkedAccount {
  id: string;
  accountType: string;
  authProvider: string;
  email?: string;
  phoneNumber?: string;
  linkedAt: string;
  isPrimary?: boolean;
}

interface InviteStats {
  stats: {
    totalInvites: number;
    acceptedInvites: number;
    pendingInvites: number;
    rewardsEarned: number;
  };
  history: any[];
}

class UserService {
  async getCurrentUser() {
    return apiClient.get(API_ENDPOINTS.users.me, {
      cacheTTL: CACHE_CONFIG.userProfileTTL
    });
  }

  async getProfile(): Promise<{ success: boolean; data: UserProfile }> {
    return apiClient.get(API_ENDPOINTS.users.profile, {
      cacheTTL: CACHE_CONFIG.userProfileTTL
    });
  }

  async updateProfile(data: Partial<UserProfile>) {
    const response = await apiClient.put(API_ENDPOINTS.users.me, data);
    // Clear profile cache
    apiClient.clearCache(API_ENDPOINTS.users.profile);
    return response;
  }

  async uploadProfileImage(image: File) {
    const formData = new FormData();
    formData.append('image', image);
    const response = await apiClient.upload(API_ENDPOINTS.users.profileImage, formData);
    // Clear profile cache
    apiClient.clearCache(API_ENDPOINTS.users.profile);
    return response;
  }

  async getContactInfo(): Promise<{ success: boolean; data: ContactInfo }> {
    return apiClient.get(API_ENDPOINTS.users.contactInfo);
  }

  async updateContactInfo(data: ContactInfo) {
    const response = await apiClient.put(API_ENDPOINTS.users.contactInfo, data);
    apiClient.clearCache(API_ENDPOINTS.users.contactInfo);
    return response;
  }

  async getSubscription() {
    return apiClient.get(API_ENDPOINTS.users.subscription, {
      cacheTTL: CACHE_CONFIG.userProfileTTL
    });
  }

  async updateSubscription(planId: string) {
    const response = await apiClient.put(API_ENDPOINTS.users.subscription, { planId });
    apiClient.clearCache(API_ENDPOINTS.users.subscription);
    return response;
  }

  async cancelSubscription() {
    const response = await apiClient.post(API_ENDPOINTS.users.cancelSubscription);
    apiClient.clearCache(API_ENDPOINTS.users.subscription);
    return response;
  }

  async getNotificationSettings(): Promise<{ success: boolean; data: NotificationSettings }> {
    return apiClient.get(API_ENDPOINTS.users.notifications);
  }

  async updateNotificationSettings(settings: NotificationSettings) {
    const response = await apiClient.put(API_ENDPOINTS.users.notifications, settings);
    apiClient.clearCache(API_ENDPOINTS.users.notifications);
    return response;
  }

  async getPaymentMethods(): Promise<{ success: boolean; data: PaymentMethod[] }> {
    return apiClient.get(API_ENDPOINTS.users.paymentMethods);
  }

  async addPaymentMethod(paymentData: any) {
    const response = await apiClient.post(API_ENDPOINTS.users.paymentMethods, paymentData);
    apiClient.clearCache(API_ENDPOINTS.users.paymentMethods);
    return response;
  }

  async setDefaultPaymentMethod(methodId: string) {
    const response = await apiClient.put(`${API_ENDPOINTS.users.paymentMethods}/${methodId}/default`);
    apiClient.clearCache(API_ENDPOINTS.users.paymentMethods);
    return response;
  }

  async deletePaymentMethod(methodId: string) {
    const response = await apiClient.delete(`${API_ENDPOINTS.users.paymentMethods}/${methodId}`);
    apiClient.clearCache(API_ENDPOINTS.users.paymentMethods);
    return response;
  }

  async getLinkedAccounts(): Promise<{ success: boolean; data: LinkedAccount[] }> {
    return apiClient.get(API_ENDPOINTS.users.linkedAccounts);
  }

  async linkAccount(authProvider: string, accountType: string) {
    const response = await apiClient.post(API_ENDPOINTS.users.linkAccount, { authProvider, accountType });
    apiClient.clearCache(API_ENDPOINTS.users.linkedAccounts);
    return response;
  }

  async unlinkAccount(linkId: string) {
    const response = await apiClient.delete(`${API_ENDPOINTS.users.linkedAccounts}/${linkId}`);
    apiClient.clearCache(API_ENDPOINTS.users.linkedAccounts);
    return response;
  }

  async getInviteStats(): Promise<{ success: boolean; data: InviteStats }> {
    return apiClient.get(API_ENDPOINTS.users.inviteStats);
  }

  async sendInvite(method: 'email' | 'sms', recipient: string, referralCode: string) {
    const response = await apiClient.post(API_ENDPOINTS.users.sendInvite, { method, recipient, referralCode });
    apiClient.clearCache(API_ENDPOINTS.users.inviteStats);
    return response;
  }

  async submitFeedback(type: 'bug' | 'feature', message: string) {
    return apiClient.post(API_ENDPOINTS.users.feedback, { type, message });
  }
}

export const userService = new UserService();